/**
 * RBAC Utilities
 * Helper functions for role-based access control and permission management
 */

// Role hierarchy levels (higher number = more permissions)
export const ROLE_LEVELS = {
  super_admin: 6,
  platform_admin: 5,
  organization_admin: 4,
  team_manager: 3,
  league_admin: 3,
  coach: 2,
  player: 1,
  fan: 0,
};

// Role display names
export const ROLE_NAMES = {
  super_admin: "Super Admin",
  platform_admin: "Platform Admin",
  organization_admin: "Organization Admin",
  team_manager: "Team Manager",
  league_admin: "League Admin",
  coach: "Coach",
  player: "Player",
  fan: "Fan",
};

// Permission categories
export const PERMISSION_CATEGORIES = {
  SYSTEM: "system",
  USERS: "users",
  ORGANIZATIONS: "organizations",
  TEAMS: "teams",
  SPORTS: "sports",
  CONTENT: "content",
  ANALYTICS: "analytics",
};

// Common permissions
export const PERMISSIONS = {
  // System permissions
  SYSTEM_READ: "system.read",
  SYSTEM_WRITE: "system.write",
  SYSTEM_ADMIN: "system.admin",

  // User permissions
  USERS_READ: "users.read",
  USERS_WRITE: "users.write",
  USERS_DELETE: "users.delete",
  USERS_ADMIN: "users.admin",

  // Organization permissions
  ORGANIZATIONS_READ: "organizations.read",
  ORGANIZATIONS_WRITE: "organizations.write",
  ORGANIZATIONS_DELETE: "organizations.delete",
  ORGANIZATIONS_ADMIN: "organizations.admin",

  // Team permissions
  TEAMS_READ: "teams.read",
  TEAMS_WRITE: "teams.write",
  TEAMS_DELETE: "teams.delete",
  TEAMS_ADMIN: "teams.admin",

  // Sports permissions
  SPORTS_READ: "sports.read",
  SPORTS_WRITE: "sports.write",
  SPORTS_DELETE: "sports.delete",
  SPORTS_ADMIN: "sports.admin",

  // Content permissions
  CONTENT_READ: "content.read",
  CONTENT_WRITE: "content.write",
  CONTENT_DELETE: "content.delete",
  CONTENT_ADMIN: "content.admin",

  // Analytics permissions
  ANALYTICS_READ: "analytics.read",
  ANALYTICS_WRITE: "analytics.write",
  ANALYTICS_ADMIN: "analytics.admin",
};

/**
 * Check if a user has a specific permission
 * @param {Array} userPermissions - User's permissions array
 * @param {string} requiredPermission - Permission to check
 * @returns {boolean}
 */
export const hasPermission = (userPermissions, requiredPermission) => {
  if (!userPermissions || !Array.isArray(userPermissions)) return false;

  return userPermissions.some((permission) => {
    // Exact match
    if (permission === requiredPermission) return true;

    // Wildcard match (e.g., 'users.*' matches 'users.read')
    if (permission.endsWith("*")) {
      const prefix = permission.slice(0, -1);
      return requiredPermission.startsWith(prefix);
    }

    return false;
  });
};

/**
 * Check if a user has any of the specified permissions
 * @param {Array} userPermissions - User's permissions array
 * @param {Array} requiredPermissions - Array of permissions to check
 * @returns {boolean}
 */
export const hasAnyPermission = (userPermissions, requiredPermissions) => {
  if (!Array.isArray(requiredPermissions)) return false;

  return requiredPermissions.some((permission) =>
    hasPermission(userPermissions, permission)
  );
};

/**
 * Check if a user has all of the specified permissions
 * @param {Array} userPermissions - User's permissions array
 * @param {Array} requiredPermissions - Array of permissions to check
 * @returns {boolean}
 */
export const hasAllPermissions = (userPermissions, requiredPermissions) => {
  if (!Array.isArray(requiredPermissions)) return false;

  return requiredPermissions.every((permission) =>
    hasPermission(userPermissions, permission)
  );
};

/**
 * Check if a user has a specific role
 * @param {string} userRole - User's role
 * @param {string} requiredRole - Role to check
 * @returns {boolean}
 */
export const hasRole = (userRole, requiredRole) => {
  return userRole === requiredRole;
};

/**
 * Check if a user has any of the specified roles
 * @param {string} userRole - User's role
 * @param {Array} requiredRoles - Array of roles to check
 * @returns {boolean}
 */
export const hasAnyRole = (userRole, requiredRoles) => {
  if (!Array.isArray(requiredRoles)) return false;
  return requiredRoles.includes(userRole);
};

/**
 * Check if a user has minimum role level (hierarchical)
 * @param {string} userRole - User's role
 * @param {string} minimumRole - Minimum required role
 * @returns {boolean}
 */
export const hasMinimumRole = (userRole, minimumRole) => {
  const userLevel = ROLE_LEVELS[userRole] || 0;
  const requiredLevel = ROLE_LEVELS[minimumRole] || 0;

  return userLevel >= requiredLevel;
};

/**
 * Check if user can access organization
 * @param {string} userRole - User's role
 * @param {Object} userOrganization - User's organization
 * @param {string} targetOrganizationId - Target organization ID
 * @returns {boolean}
 */
export const hasOrganizationAccess = (
  userRole,
  userOrganization,
  targetOrganizationId
) => {
  // System level roles have access to all organizations
  if (hasAnyRole(userRole, ["super_admin", "platform_admin"])) {
    return true;
  }

  // No specific organization required
  if (!targetOrganizationId) return true;

  // Check if user belongs to the organization
  return userOrganization?.id === targetOrganizationId;
};

/**
 * Check if user can access team
 * @param {string} userRole - User's role
 * @param {Object} userTeam - User's team
 * @param {Object} userOrganization - User's organization
 * @param {string} targetTeamId - Target team ID
 * @param {string} teamOrganizationId - Team's organization ID
 * @returns {boolean}
 */
export const hasTeamAccess = (
  userRole,
  userTeam,
  userOrganization,
  targetTeamId,
  teamOrganizationId
) => {
  // System and organization level roles
  if (hasMinimumRole(userRole, "organization_admin")) {
    return hasOrganizationAccess(
      userRole,
      userOrganization,
      teamOrganizationId
    );
  }

  // No specific team required
  if (!targetTeamId) return true;

  // Check if user belongs to the team
  return userTeam?.id === targetTeamId;
};

/**
 * Get user's accessible menu items based on role and permissions
 * @param {string} userRole - User's role
 * @param {Array} userPermissions - User's permissions
 * @returns {Array} Accessible menu items
 */
export const getAccessibleMenuItems = (userRole, userPermissions) => {
  const menuItems = [];

  // Dashboard (always accessible)
  menuItems.push({
    id: "dashboard",
    name: "Dashboard",
    path: "/dashboard",
    icon: "dashboard",
  });

  // System Management (Super Admin only)
  if (hasRole(userRole, "super_admin")) {
    menuItems.push({
      id: "system",
      name: "System Management",
      path: "/admin/system",
      icon: "settings",
      children: [
        {
          id: "system-overview",
          name: "Overview",
          path: "/admin/system/overview",
        },
        {
          id: "system-config",
          name: "Configuration",
          path: "/admin/system/config",
        },
        { id: "system-logs", name: "Logs", path: "/admin/system/logs" },
      ],
    });
  }

  // User Management
  if (
    hasAnyPermission(userPermissions, [
      PERMISSIONS.USERS_READ,
      PERMISSIONS.USERS_ADMIN,
    ])
  ) {
    menuItems.push({
      id: "users",
      name: "User Management",
      path: "/admin/users",
      icon: "users",
    });
  }

  // Organization Management
  if (
    hasAnyPermission(userPermissions, [
      PERMISSIONS.ORGANIZATIONS_READ,
      PERMISSIONS.ORGANIZATIONS_ADMIN,
    ])
  ) {
    menuItems.push({
      id: "organizations",
      name: "Organizations",
      path: "/admin/organizations",
      icon: "building",
    });
  }

  // Team Management
  if (
    hasAnyPermission(userPermissions, [
      PERMISSIONS.TEAMS_READ,
      PERMISSIONS.TEAMS_ADMIN,
    ])
  ) {
    menuItems.push({
      id: "teams",
      name: "Teams",
      path: "/admin/teams",
      icon: "team",
    });
  }

  // Sports Management
  if (
    hasAnyPermission(userPermissions, [
      PERMISSIONS.SPORTS_READ,
      PERMISSIONS.SPORTS_ADMIN,
    ])
  ) {
    menuItems.push({
      id: "sports",
      name: "Sports",
      path: "/admin/sports",
      icon: "trophy",
    });
  }

  // Analytics
  if (
    hasAnyPermission(userPermissions, [
      PERMISSIONS.ANALYTICS_READ,
      PERMISSIONS.ANALYTICS_ADMIN,
    ])
  ) {
    menuItems.push({
      id: "analytics",
      name: "Analytics",
      path: "/admin/analytics",
      icon: "chart",
    });
  }

  return menuItems;
};

// ── User-object helpers (mirror the backend checks) ─────────────────────────

/** Roles the backend lets create leagues and tournaments
 *  (requireRole(['league_admin','organization_admin']) + super/platform bypass). */
export const COMPETITION_CREATOR_ROLES = [
  "league_admin",
  "organization_admin",
  "platform_admin",
  "super_admin",
];

const SYSTEM_ADMIN_ROLES = ["super_admin", "platform_admin"];

/** All role names a user object carries (role, role_name, roleName, roles[]). */
export const getUserRoles = (user) => {
  if (!user) return [];
  const roles = [user.role, user.role_name, user.roleName];
  if (Array.isArray(user.roles)) {
    user.roles.forEach((r) => roles.push(typeof r === "string" ? r : r?.name));
  }
  return [...new Set(roles.filter(Boolean).map((r) => (r === "superadmin" ? "super_admin" : r)))];
};

/** True when the user holds one of these exact roles. */
export const userHasAnyRole = (user, roles) =>
  getUserRoles(user).some((r) => roles.includes(r));

/** Permission names of a user (permissions may be strings or { name }). */
export const getUserPermissionNames = (user) =>
  Array.isArray(user?.permissions)
    ? user.permissions.map((p) => (typeof p === "string" ? p : p?.name)).filter(Boolean)
    : [];

export const isSystemAdmin = (user) => userHasAnyRole(user, SYSTEM_ADMIN_ROLES);

/** Whether the user created this league / tournament / match. */
export const isCreatorOf = (user, entity) => {
  if (!user || !entity) return false;
  if (entity.created_by != null && user.id != null &&
      String(entity.created_by) === String(user.id)) return true;
  if (entity.created_by_uuid && user.uuid && entity.created_by_uuid === user.uuid) return true;
  if (entity.created_by_short_code && user.short_code &&
      String(entity.created_by_short_code) === String(user.short_code)) return true;
  return false;
};

/** Create league / tournament pages and buttons. */
export const canCreateCompetition = (user) =>
  userHasAnyRole(user, COMPETITION_CREATOR_ROLES);

/** Manage (edit / delete / status / schedule) a league: creator or super/platform admin. */
export const canManageLeague = (user, league) =>
  isCreatorOf(user, league) || isSystemAdmin(user);

/** /sports-admin: super_admin, or a sports write/admin permission (backend sportsRoutes). */
export const canUseSportsAdmin = (user) => {
  if (userHasAnyRole(user, ["super_admin"])) return true;
  const perms = getUserPermissionNames(user);
  return ["sports.write", "sports.admin", "sports.*"].some((p) => perms.includes(p));
};

/**
 * Format role name for display
 * @param {string} role - Role key
 * @returns {string} Formatted role name
 */
export const formatRoleName = (role) => {
  return ROLE_NAMES[role] || role;
};

/**
 * Get role color for UI display
 * @param {string} role - Role key
 * @returns {string} Color class or hex code
 */
export const getRoleColor = (role) => {
  const colors = {
    super_admin: "#dc2626", // Red
    platform_admin: "#7c3aed", // Purple
    organization_admin: "#2563eb", // Blue
    team_manager: "#059669", // Green
    coach: "#d97706", // Orange
    player: "#4f46e5", // Indigo
    fan: "#6b7280", // Gray
  };

  return colors[role] || "#6b7280";
};

/**
 * Check if action is allowed based on context
 * @param {Object} user - User object with role and permissions
 * @param {string} action - Action to check (permission)
 * @param {Object} context - Context object (organization, team, etc.)
 * @returns {boolean}
 */
export const isActionAllowed = (user, action, context = {}) => {
  // Check basic permission
  if (!hasPermission(user.permissions, action)) {
    return false;
  }

  // Check organization access if required
  if (
    context.organizationId &&
    !hasOrganizationAccess(user.role, user.organization, context.organizationId)
  ) {
    return false;
  }

  // Check team access if required
  if (
    context.teamId &&
    !hasTeamAccess(
      user.role,
      user.team,
      user.organization,
      context.teamId,
      context.teamOrganizationId
    )
  ) {
    return false;
  }

  return true;
};

const rbac = {
  ROLE_LEVELS,
  ROLE_NAMES,
  PERMISSION_CATEGORIES,
  PERMISSIONS,
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  hasRole,
  hasAnyRole,
  hasMinimumRole,
  hasOrganizationAccess,
  hasTeamAccess,
  getAccessibleMenuItems,
  formatRoleName,
  getRoleColor,
  isActionAllowed,
};

export default rbac;
